<script lang="ts">
    export let ariaLabel: string;
    export let isEllipseArtwork: boolean;
</script>

<div
    class="top-search-lockup"
    data-testid="top-search-result"
    aria-label={ariaLabel}
    class:top-search-lockup--ellipse={isEllipseArtwork}
>
    {#if $$slots['primary-action']}
        <div class="top-search-lockup__action">
            <slot name="primary-action" />
        </div>
    {/if}

    <div class="top-search-lockup__content">
        <div class="top-search-lockup__artwork">
            <slot name="artwork" artworkProps={{ forceFullWidth: false }} />
        </div>

        <div class="top-search-lockup__description">
            <slot name="description-content" />
        </div>
    </div>

    <div class="top-search-lockup__icons">
        <slot name="secondary-buttons" />
    </div>
</div>

<style lang="scss">
    @use '@amp/web-shared-styles/sasskit-stylekit/ac-sasskit-config';
    @use 'ac-sasskit/core/selectors' as *;
    @use '@amp/web-shared-styles/app/core/globalvars' as *;
    @use '@amp/web-shared-styles/app/core/mixins/aspect-ratio' as *;
    @use '@amp/web-shared-styles/app/core/mixins/after-shadow' as *;
    @use '@amp/web-shared-styles/app/core/mixins/absolute-center' as *;
    @use 'amp/stylekit/core/colors' as *;

    $top-search-lockup-image-size-small: 60px;
    $top-search-lockup-image-size-medium: 90px;
    $top-search-lockup-image-size-xlarge: 110px;
    $top-search-border-radius: 8px;

    .top-search-lockup {
        // Lockup image size by viewport, used for both content and artwork blocks
        --topSearchLockupImageSize: #{$top-search-lockup-image-size-small};
        padding: 14px 12px;
        height: auto;
        min-width: 0;
        display: grid;
        grid-template-columns: 1fr auto;
        grid-template-areas: 'content icons';
        align-items: center;
        border-radius: $top-search-border-radius;
        box-shadow: 0 4px 14px rgba(0, 0, 0, 0.1);
        position: relative;

        // Use swatch for background color to prevent darkened platter in Increased Contrast mode
        background-color: swatch(systemQuaternary, light);
        z-index: var(--z-default);

        @media (prefers-color-scheme: dark) {
            background-color: var(--systemQuaternary);
        }

        &::after {
            --afterShadowBorderRadius: #{$top-search-border-radius};

            @include after-shadow;
        }

        @media (prefers-color-scheme: dark) {
            background-color: swatch(systemQuaternary, dark);
        }

        @media (--medium) {
            --topSearchLockupImageSize: #{$top-search-lockup-image-size-medium};

            // Reduce artwork size when drawer is open.
            // Only applies to `medium` and `large` viewports.
            @include feature-detect(is-drawer-open) {
                --topSearchLockupImageSize: #{$top-search-lockup-image-size-small};
            }
        }

        @media (--xlarge) {
            --topSearchLockupImageSize: #{$top-search-lockup-image-size-xlarge};
        }
    }

    .top-search-lockup__content {
        min-height: var(--topSearchLockupImageSize);
        display: grid;
        grid-template-columns: auto 1fr auto;
        grid-gap: 12px;
        margin-inline-end: -12px;
        align-items: center;

        @media (--medium) {
            grid-gap: 14px;
            margin-inline-end: -14px;
        }

        @media (--xlarge) {
            grid-gap: 20px;
        }
    }

    .top-search-lockup__description {
        flex: 1;
        flex-shrink: 1;
        display: block;
        overflow: hidden;
        pointer-events: none;

        + .top-search-lockup__icon {
            margin-inline-start: 10px;
        }
    }

    // `div` is required here for specificty overwrite
    div.top-search-lockup__artwork {
        position: relative;
        z-index: var(--z-default);

        .top-search-lockup & {
            &,
            > :global(.artwork-component) {
                border-radius: var(
                    --global-border-radius-medium,
                    #{$global-border-radius-medium}
                );
            }
        }

        .top-search-lockup--ellipse & {
            &,
            > :global(.artwork-component) {
                border-radius: 50%;
            }
        }

        > :global(.artwork-component) {
            --override-placeholder-bg-color: var(--artwork-bg-color);

            @include aspect-ratio($width: $top-search-lockup-image-size-small);

            @media (--medium) {
                @include aspect-ratio(
                    $width: $top-search-lockup-image-size-medium
                );

                // Reduce artwork size when drawer is open.
                // Only applies to `medium` and `large` viewports.
                @include feature-detect(is-drawer-open) {
                    @include aspect-ratio(
                        $width: $top-search-lockup-image-size-small
                    );
                }
            }

            @media (--large) {
                @include aspect-ratio($height: var(--topSearchLockupImageSize));
            }
        }

        // Any images that are portrait should be constrained by their expected height at all times
        > :global(.artwork-component--orientation-portrait) {
            @include aspect-ratio($height: var(--topSearchLockupImageSize));
        }
    }

    .top-search-lockup__icons {
        position: absolute;
        visibility: hidden; // Hide cloud buttons in mobile viewport
        z-index: calc(var(--z-default) + 1);

        @media (--sidebar-visible) {
            top: initial;
            bottom: 0;
            visibility: visible;
            inset-inline-end: 6px;
        }

        @media (--medium) {
            bottom: 8px;
            inset-inline-end: 10px;
        }

        @media (--xlarge) {
            bottom: 14px;
        }
    }

    .top-search-lockup__action {
        border-radius: inherit;

        @include absolute-center;

        // Cover entire lockup with segue or play button
        :global(a),
        :global(button) {
            font-size: 0; // Visually hide inner text of link/button
            border-radius: inherit;
            display: block;
            width: 100%;
            height: 100%;
        }

        // Raise click action above artist artwork
        .top-search-lockup--ellipse & {
            z-index: calc(var(--z-default) + 1);
        }
    }
</style>
